// =====================================================================
// src/pages/intl-acca/CalendarPage.tsx
// =====================================================================
// Multi-tournament index. Groups all known tournaments by lifecycle
// state (ACTIVE / PRE_TOURNAMENT / POST_TOURNAMENT / ARCHIVED).
//
// Routes mounted at: /intl-acca/calendar
//
// This is the answer to "why is this locked to only AFCON?" — the
// platform spans WC 2026, AFCON 2027, qualifier windows, Copa,
// Gold Cup, UEFA NL, etc. The Calendar is the entry point.

import type { IntlAccaTournament } from "../../db/intl-acca-types";
import { HonestEmptyState } from "../../components/intl-acca/HonestEmptyState";
import { NEXT_STATE } from "../../intl-acca/useIntlAccaTournamentAdmin.ts";

/** Founder-only lifecycle controls (PRD §5). Absent / isAdmin=false ⇒
 *  no admin chrome renders at all. */
export interface CalendarAdminVM {
  isAdmin: boolean;
  busyId: string | null;
  error: string | null;
  advance: (
    id: string,
    toStatus: IntlAccaTournament["status"],
    passActive?: boolean,
  ) => Promise<boolean>;
}

export interface CalendarPageProps {
  loading: boolean;
  error: string | null;
  tournaments: IntlAccaTournament[];
  /** Click a card to switch the active tournament context + go to its home. */
  onSelectTournament?: (tournamentId: string) => void;
  /** Founder lifecycle control. Omit for the public (non-admin) view. */
  admin?: CalendarAdminVM;
}

const STATE_LABEL: Record<IntlAccaTournament["status"], string> = {
  ACTIVE:           "Active now",
  PRE_TOURNAMENT:   "Pre-tournament · Pass on sale",
  POST_TOURNAMENT:  "Post-tournament · Debrief generating",
  ARCHIVED:         "Archive · Reference data",
};
const STATE_COLOR: Record<IntlAccaTournament["status"], string> = {
  ACTIVE:           "var(--pos)",
  PRE_TOURNAMENT:   "var(--accent)",
  POST_TOURNAMENT:  "var(--info)",
  ARCHIVED:         "var(--text-tertiary)",
};
const STATE_ORDER: IntlAccaTournament["status"][] = [
  "ACTIVE",
  "PRE_TOURNAMENT",
  "POST_TOURNAMENT",
  "ARCHIVED",
];

export function CalendarPage(props: CalendarPageProps): JSX.Element {
  const { loading, error, tournaments, onSelectTournament, admin } = props;

  if (loading) {
    return (
      <div className="ia-root ia-page-pad" aria-busy="true">
        <div className="ia-eyebrow">Loading calendar…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState variant="custom" title="Calendar unavailable." body={error} />
      </div>
    );
  }

  const grouped: Record<IntlAccaTournament["status"], IntlAccaTournament[]> = {
    ACTIVE: [], PRE_TOURNAMENT: [], POST_TOURNAMENT: [], ARCHIVED: [],
  };
  for (const t of tournaments) grouped[t.status].push(t);

  return (
    <div className="ia-root">
      <header className="ia-page-header">
        <div>
          <div className="ia-eyebrow">International calendar · all windows</div>
          <h1>
            The Calendar.{" "}
            <em>Every window. One pricing engine.</em>
          </h1>
          <p className="ia-sub">
            STRIKZA International Acca runs across every meaningful
            international window — World Cup, AFCON, Copa, Gold Cup,
            qualifiers, UEFA Nations League. Pick a tournament to
            activate its hub, slip engine, and DNA roster.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
          <span className="ia-pill ia-pill-pos">
            {grouped.ACTIVE.length} ACTIVE
          </span>
          <span className="ia-pill ia-pill-amber">
            {grouped.PRE_TOURNAMENT.length} ON SALE
          </span>
          <span className="ia-pill ia-pill-info">
            {grouped.POST_TOURNAMENT.length} POST
          </span>
          <span className="ia-pill ia-pill-ghost">
            {grouped.ARCHIVED.length} ARCHIVE
          </span>
        </div>
      </header>

      <div className="ia-page-pad">
        {admin?.isAdmin && admin.error ? (
          <div
            role="alert"
            style={{
              marginBottom: 16,
              padding: "10px 14px",
              border: "1px solid var(--neg, #c0392b)",
              borderRadius: 8,
              fontSize: "var(--sz-body-sm)",
              color: "var(--neg, #c0392b)",
            }}
          >
            {admin.error}
          </div>
        ) : null}
        {tournaments.length === 0 ? (
          <HonestEmptyState
            variant="custom"
            title="No tournaments registered."
            body="The intl_acca_tournaments table is empty. Seed it via the founder admin RPC."
          />
        ) : (
          STATE_ORDER.map((state) => {
            const list = grouped[state];
            if (list.length === 0) return null;
            return (
              <section key={state} style={{ marginBottom: 48 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    marginBottom: 18,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: STATE_COLOR[state],
                    }}
                  />
                  <span
                    className="ia-mono"
                    style={{
                      fontSize: "var(--sz-mono-sm)",
                      letterSpacing: "0.22em",
                      textTransform: "uppercase",
                      color: STATE_COLOR[state],
                    }}
                  >
                    {STATE_LABEL[state]}
                  </span>
                  <span
                    className="ia-mono"
                    style={{
                      fontSize: "var(--sz-mono-xs)",
                      color: "var(--text-tertiary)",
                      letterSpacing: "0.18em",
                    }}
                  >
                    · {list.length}
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
                    gap: 16,
                  }}
                >
                  {list.map((t) => (
                    <div key={t.id}>
                      <TournamentCard
                        tournament={t}
                        onSelect={() => onSelectTournament?.(t.id)}
                      />
                      {admin?.isAdmin ? (
                        <AdminLifecycleControl tournament={t} admin={admin} />
                      ) : null}
                    </div>
                  ))}
                </div>
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}

function TournamentCard({
  tournament: t, onSelect,
}: {
  tournament: IntlAccaTournament;
  onSelect: () => void;
}): JSX.Element {
  const dateRange = `${new Date(t.start_date).toLocaleDateString("en-GB", {
    month: "short", day: "numeric",
  })} – ${new Date(t.end_date).toLocaleDateString("en-GB", {
    month: "short", day: "numeric", year: "numeric",
  })}`;

  return (
    <button type="button" onClick={onSelect} className="ia-cal-card" style={{ textAlign: "left", width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 18,
        }}
      >
        <div>
          <span
            className="ia-mono"
            style={{
              fontSize: "var(--sz-mono-xs)",
              color: STATE_COLOR[t.status],
              letterSpacing: "0.22em",
              textTransform: "uppercase",
            }}
          >
            {t.status.replace("_", " ")} · {t.region}
          </span>
          <div
            style={{
              fontFamily: "var(--sz-font-head)",
              fontSize: "var(--sz-head-md)",
              fontWeight: 500,
              marginTop: 8,
              color: "var(--text)",
              lineHeight: 1.05,
              letterSpacing: "-0.01em",
            }}
          >
            {t.name}
          </div>
          <div
            className="ia-mono"
            style={{
              fontSize: "var(--sz-mono-sm)",
              color: "var(--text-tertiary)",
              letterSpacing: "0.16em",
              marginTop: 8,
            }}
          >
            {dateRange}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 20,
          paddingTop: 16,
          borderTop: "1px solid var(--border-faint)",
        }}
      >
        <div>
          <div className="ia-metric-label">PASS</div>
          <div
            style={{
              fontFamily: "var(--sz-font-display)",
              fontSize: 22,
              color: "var(--accent-text)",
              lineHeight: 1,
              marginTop: 4,
            }}
          >
            {t.pass_price_usd == null ? "—" : `$${t.pass_price_usd}`}
          </div>
        </div>
        <div>
          <div className="ia-metric-label">PRIORITY</div>
          <div
            className="ia-mono"
            style={{
              fontSize: "var(--sz-mono-md)",
              color: "var(--text)",
              lineHeight: 1,
              marginTop: 4,
              letterSpacing: "0.18em",
            }}
          >
            {t.priority ?? "—"}
          </div>
        </div>
        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <div className="ia-metric-label">STATUS</div>
          <div
            className="ia-mono"
            style={{
              fontSize: "var(--sz-mono-md)",
              color: STATE_COLOR[t.status],
              lineHeight: 1,
              marginTop: 4,
              letterSpacing: "0.18em",
            }}
          >
            {t.pass_active ? "ON SALE" : "NOT YET"}
          </div>
        </div>
      </div>

      {t.notes ? (
        <p
          style={{
            marginTop: 14,
            fontSize: "var(--sz-body-sm)",
            color: "var(--text-secondary)",
            lineHeight: 1.55,
          }}
        >
          {t.notes}
        </p>
      ) : null}
    </button>
  );
}

// Founder-only lifecycle strip rendered beneath each card. Forward-only
// (mirrors the RPC); the server re-checks is_strikza_admin() so this is
// UX, not the trust boundary.
function AdminLifecycleControl({
  tournament: t,
  admin,
}: {
  tournament: IntlAccaTournament;
  admin: CalendarAdminVM;
}): JSX.Element {
  const next = NEXT_STATE[t.status];
  const busy = admin.busyId === t.id;
  return (
    <div
      style={{
        marginTop: 8,
        padding: "10px 14px",
        border: "1px dashed var(--border-faint)",
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <span
        className="ia-mono"
        style={{
          fontSize: "var(--sz-mono-xs)",
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: "var(--text-tertiary)",
        }}
      >
        ★ Founder
      </span>
      {next ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => void admin.advance(t.id, next)}
          className="ia-pill ia-pill-outline"
          style={{ cursor: busy ? "wait" : "pointer", padding: "6px 12px", fontSize: 10.5 }}
        >
          {busy ? "Working…" : `Advance → ${next.replace("_", " ")}`}
        </button>
      ) : (
        <span
          className="ia-mono"
          style={{ fontSize: "var(--sz-mono-xs)", color: "var(--text-tertiary)" }}
        >
          Terminal (archived)
        </span>
      )}
      <button
        type="button"
        disabled={busy}
        onClick={() => void admin.advance(t.id, t.status, !t.pass_active)}
        className="ia-pill ia-pill-ghost"
        style={{ cursor: busy ? "wait" : "pointer", padding: "6px 12px", fontSize: 10.5 }}
      >
        {t.pass_active ? "Pass: ON → turn off" : "Pass: OFF → put on sale"}
      </button>
    </div>
  );
}

// =====================================================================
// DATA BINDING CONTRACT
// =====================================================================
//
// const { data: tournaments } = await sb.from('intl_acca_tournaments')
//                                     .select('*').order('start_date', { ascending: false });
//
// onSelectTournament: host updates the active-tournament context
// (e.g. via a Zustand store or a useTournamentSwitcher hook), then
// navigates to /intl-acca/:id.
